import { notificationNavigation } from '../../../utilities/notifications/notificationNavigation';
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import EventNotification from "../../../interfaces/eventNotification/EventNotification";
import { AcceptRejectMeetingInvitationAsync } from "../../../services/calendarService/EventService";
import { dispatchMeetingInvitationStateChanged } from "../../../utilities/notifications/meetingInvitationEvents";

interface MeetingInvitationActionsProps {
  notification: EventNotification;
  compact?: boolean;
  onResponded?: (status: "Accepted" | "Declined") => void;
  onNavigate?: () => void;
}

const MeetingInvitationActions: React.FC<MeetingInvitationActionsProps> = ({
  notification,
  compact = false,
  onResponded,
  onNavigate,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [requestState, setRequestState] = useState<"idle" | "ACCEPT" | "DECLINE">("idle");
  const [localStatus, setLocalStatus] = useState<string | null>(notification.actionLabel ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const meetingResource = notification.notificationResources?.find(
    (resource) => resource.resourceType.toLowerCase() === "meeting",
  );
  const meetingPath = notification.actionUrl || meetingResource?.resourceId;
  const eventId = useMemo(() => {
    if (!meetingPath) return null;
    const queryIndex = meetingPath.indexOf("?");
    if (queryIndex < 0) return null;
    return new URLSearchParams(meetingPath.slice(queryIndex + 1)).get("eventId");
  }, [meetingPath]);

  if (notification.type.toLowerCase() !== "meeting.invitation") return null;

  const normalizedStatus = localStatus?.trim().toLowerCase();
  const hasResponded = normalizedStatus === "accepted" || normalizedStatus === "declined";
  const canRespond = notification.allowApproval === true && Boolean(eventId) && !hasResponded;

  const respond = async (action: "ACCEPT" | "DECLINE") => {
    if (!currentUser || !eventId || requestState !== "idle") return;

    setRequestState(action);
    setErrorMessage(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await AcceptRejectMeetingInvitationAsync(eventId, action, token);
      if (!response.success) throw new Error(response.message || "Unable to update this invitation.");

      const status = action === "ACCEPT" ? "Accepted" : "Declined";
      setLocalStatus(status);
      dispatchMeetingInvitationStateChanged({
        notificationId: notification.id,
        status,
      });
      onResponded?.(status);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to update this invitation.");
    } finally {
      setRequestState("idle");
    }
  };

  if (hasResponded) {
    return (
      <div className={`${compact ? "mt-1" : "mt-2"} text-[10px] italic text-gray-500 dark:text-zinc-400`}>
        {localStatus}
      </div>
    );
  }

  return (
    <div
      className={`${compact ? "mt-2" : "mt-3"} flex flex-wrap items-center gap-2`}
      data-toast-interactive="true"
      onClick={(event) => event.stopPropagation()}
    >
      {canRespond && (
        <>
          <button
            type="button"
            disabled={requestState !== "idle"}
            onClick={() => void respond("ACCEPT")}
            className={`${compact ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-[11px]"} rounded-lg bg-emerald-600 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60`}
          >
            {requestState === "ACCEPT" ? "Acceptingâ€¦" : "Accept"}
          </button>
          <button
            type="button"
            disabled={requestState !== "idle"}
            onClick={() => void respond("DECLINE")}
            className={`${compact ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-[11px]"} rounded-lg border border-gray-200 bg-white font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 disabled:cursor-wait disabled:opacity-60`}
          >
            {requestState === "DECLINE" ? "Decliningâ€¦" : "Decline"}
          </button>
        </>
      )}

      {meetingPath && (
        <button
          type="button"
          onClick={() => {
            navigate(notificationNavigation(meetingPath));
            onNavigate?.();
          }}
          className="px-1 text-[10px] font-semibold text-[#6D4DB3] hover:underline dark:text-purple-300"
        >
          View details
        </button>
      )}

      {errorMessage && (
        <span className="basis-full text-[10px] text-red-600 dark:text-red-400">{errorMessage}</span>
      )}
    </div>
  );
};

export default MeetingInvitationActions;
